import type { DriveStep } from "driver.js";

import { runTour, type TourOutcome } from "../tour/runTour";

/**
 * The story-map builder coach tour.
 *
 * Deliberately orienting, not instructional: it names the three regions and the
 * two ideas people don't guess (a scene is a scroll step; the map's *mode*
 * decides what the map even is), then gets out of the way. Step-by-step "now
 * click here" tours are not retained, and the per-control explanations live in
 * HelpTip popovers where they can be read at the moment they're needed.
 *
 * Anchors are explicit `data-tour` attributes rather than MUI's generated class
 * names — those are Emotion hashes, so a selector-based tour would break
 * silently on any refactor. A renamed `data-tour` shows up in review instead.
 */

/** Anchor names; each must appear as a `data-tour` attribute in the builder. */
export const TOUR_ANCHOR = {
  sceneRail: "scene-rail",
  sceneEditor: "scene-editor",
  map: "map-pane",
  mapMode: "map-mode",
  camera: "camera-capture",
  publish: "publish-button",
  help: "help-button",
} as const;

const at = (anchor: string) => `[data-tour="${anchor}"]`;

export const STORY_TOUR_STEPS: DriveStep[] = [
  {
    element: at(TOUR_ANCHOR.map),
    popover: {
      title: "This is what your reader sees",
      description:
        "The map fills the reader's screen while your text scrolls past it. " +
        "Everything in this builder is about deciding what it shows, and when.",
      side: "left",
      align: "center",
    },
  },
  {
    element: at(TOUR_ANCHOR.sceneRail),
    popover: {
      title: "Scenes are the steps",
      description:
        "Each numbered box is one scene — one stop on the reader's scroll. " +
        "The map moves to that scene's saved view as they reach it. Add, " +
        "reorder and delete scenes here.",
      side: "right",
      align: "start",
    },
  },
  {
    element: at(TOUR_ANCHOR.sceneEditor),
    popover: {
      title: "The words beside the map",
      description:
        "This column is the narrative for the selected scene. Build it from " +
        "blocks — headings, text, figures and images — using the palette at " +
        "the bottom.",
      side: "right",
      align: "start",
    },
  },
  {
    element: at(TOUR_ANCHOR.mapMode),
    popover: {
      title: "Map mode is the big choice",
      description:
        "It decides what the map is for this scene: project layers, a " +
        "before/after split with a drag handle, or a fire simulation playing " +
        "over time. The settings below change to match.",
      side: "left",
      align: "start",
    },
  },
  {
    element: at(TOUR_ANCHOR.camera),
    popover: {
      title: "Each scene remembers a view",
      description:
        "Pan and zoom the map to where you want the reader to land, then press " +
        "this. That framing is saved on this scene alone.",
      side: "left",
      align: "start",
    },
  },
  {
    element: at(TOUR_ANCHOR.publish),
    popover: {
      title: "Publishing freezes a copy",
      description:
        "Preview reads your draft. Publishing takes a frozen snapshot and gives " +
        "you a link — so what you send out never changes underneath the reader, " +
        "even as you keep editing.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: at(TOUR_ANCHOR.help),
    popover: {
      title: "That's the tour",
      description:
        "Replay it any time from here. The small ? icons through the panels " +
        "explain individual settings.",
      side: "bottom",
      align: "end",
    },
  },
];

export type StoryTourOutcome = TourOutcome;

/**
 * Run the tour once. Resolves with how it ended so the caller can record it.
 *
 * `skipMissingElement` (set in runTour) matters here: the camera and map-mode
 * anchors only exist once a scene has loaded, and compare/animation rails
 * come and go with the mode. A missing anchor should cost that one step, not
 * the whole tour.
 */
export function runStoryTour(
  onFinish: (outcome: StoryTourOutcome) => void
): () => void {
  return runTour(STORY_TOUR_STEPS, onFinish);
}
